import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AxiosError } from 'axios'
import fleetAPI from '../service/fleetAPI'
import { DivergenceResponse, PushResultEntry } from '../types/fleet.types'
import { CallbackFunction } from './useFleets'

// GET /fleets/{id}/divergence — per-momi cloud-vs-momi version state (plan §4 F3).
// Wire contract §7: the response is the wrapper { fleet_id, cloud_version,
// cloud_hash, divergence: [...] }; consumers read `.divergence`.
export const useFleetDivergence = (fleetId?: string) => {
  return useQuery<DivergenceResponse>({
    queryKey: ['fleetDivergence', fleetId],
    queryFn: () => fleetAPI.getDivergence(fleetId as string),
    enabled: Boolean(fleetId),
    refetchInterval: 15_000,
  })
}

// POST /fleets/{id}/push — overwrite: fan out the cloud profile to members.
// Returns per-device push results for partial-failure surfacing.
export const usePushFleet = () => {
  const queryClient = useQueryClient()
  // Id de l'invalidation différée : conservé pour être nettoyé au démontage,
  // afin d'éviter un invalidateQueries sur un composant démonté (fuite/warn).
  const delayedRefetchRef = useRef<ReturnType<typeof setTimeout>>()
  useEffect(
    () => () => {
      if (delayedRefetchRef.current) clearTimeout(delayedRefetchRef.current)
    },
    [],
  )
  const mutation = useMutation<
    PushResultEntry[],
    AxiosError,
    { fleetId: string; callbackFunc?: CallbackFunction }
  >({
    mutationFn: ({ fleetId, callbackFunc }) =>
      fleetAPI
        .pushFleet(fleetId)
        .then((result) => {
          // Refetch divergence immediately so the panel reflects the push.
          queryClient.invalidateQueries(['fleetDivergence', fleetId])
          // NOTE: the momi_version only changes once the momi has actually
          // APPLIED the new config and emitted its asynchronous version_report
          // (over WS/MQTT), which arrives a few seconds AFTER the push HTTP call
          // returns. So do a second delayed refetch to catch that report. If no
          // momi is connected, no report arrives and the row stays "Diverged" —
          // that is expected, not a UI bug.
          if (delayedRefetchRef.current) clearTimeout(delayedRefetchRef.current)
          delayedRefetchRef.current = setTimeout(() => {
            queryClient.invalidateQueries(['fleetDivergence', fleetId])
          }, 3000)
          callbackFunc?.({ result, error: undefined })
          return result
        })
        .catch((error) => {
          callbackFunc?.({ result: undefined, error })
          return Promise.reject(error)
        }),
  })
  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    data: mutation.data,
    error: mutation.error,
  }
}

// POST /fleets/{id}/pull/{momi_id} — download: overwrite the cloud profile from
// a momi's stored copy.
export const usePullFleet = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      fleetId,
      momiId,
      callbackFunc,
    }: {
      fleetId: string
      momiId: string
      callbackFunc?: CallbackFunction
    }) =>
      fleetAPI
        .pullFromMomi(fleetId, momiId)
        .then((result) => {
          queryClient.invalidateQueries(['fleetDivergence', fleetId])
          queryClient.invalidateQueries(['fleet', fleetId])
          queryClient.invalidateQueries('fleets')
          callbackFunc?.({ result, error: undefined })
          return result
        })
        .catch((error) => {
          callbackFunc?.({ result: undefined, error })
          return Promise.reject(error)
        }),
  })
  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    error: mutation.error as AxiosError,
  }
}
